import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Rocket, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface PromoteButtonProps {
  productId: string;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg";
  className?: string;
  onPromoted?: () => void;
}

const PromoteButton = ({
  productId,
  variant = "outline",
  size = "sm",
  className,
  onPromoted,
}: PromoteButtonProps) => {
  const { t } = useTranslation();
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast({ title: t("promote.errorAuth"), variant: "destructive" });
      navigate("/login");
      return;
    }
    setOpen(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("promote_product" as any, {
        _product_id: productId,
      });
      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (!result?.success) {
        if (result?.error === "insufficient_credits") {
          toast({ title: t("promote.errorCredits"), variant: "destructive" });
        } else if (result?.error === "limit_reached") {
          toast({ title: t("promote.errorLimit"), variant: "destructive" });
        } else {
          toast({ title: t("promote.errorGeneric"), variant: "destructive" });
        }
        return;
      }
      toast({ title: t("promote.success") });
      await refreshProfile();
      onPromoted?.();
      setOpen(false);
    } catch (err) {
      toast({ title: t("promote.errorGeneric"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleClick}
        className={className}
        type="button"
      >
        <Rocket className="h-3.5 w-3.5" />
        {t("promote.button")}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("promote.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("promote.confirmDesc")}
              <br />
              <span className="font-medium text-foreground mt-2 inline-block">
                {t("promote.yourCredits")} {profile?.feature_credits ?? 0}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>
              {t("promote.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={loading || (profile?.feature_credits ?? 0) < 1}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("promote.confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PromoteButton;
